import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  fetchAllTemplates,
  fetchTemplateVersions,
  fetchTemplateById,
  createTemplate,
  activateTemplate,
  previewTemplate,
  deleteTemplate,
  fetchTemplateMetadata,
  setSelectedTemplate,
  clearError,
  clearPreview
} from '@/store/slices/notificationTemplateSlice'
import {
  CreateNotificationTemplateRequest,
  TemplatePreviewRequest
} from '@shared/types/notificationTemplate'

export const useNotificationTemplates = () => {
  const dispatch = useAppDispatch()
  const {
    templates,
    selectedTemplate,
    templateMetadata,
    preview,
    loading,
    error
  } = useAppSelector(state => state.notificationTemplates)

  // Fetch metadata on mount
  useEffect(() => {
    if (!templateMetadata) {
      dispatch(fetchTemplateMetadata())
    }
  }, [dispatch, templateMetadata])

  // Group templates by name
  const templateGroups = useMemo(() => {
    const groups: Record<string, typeof templates> = {}

    // Handle undefined or null templates
    if (!templates || !Array.isArray(templates)) {
      return groups
    }

    templates.forEach(template => {
      if (!groups[template.name]) {
        groups[template.name] = []
      }
      groups[template.name].push(template)
    })

    // Sort each group by version descending
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => b.version - a.version)
    })

    return groups
  }, [templates])

  // Get active template for each group
  const activeTemplates = useMemo(() => {
    const active: Record<string, typeof templates[0] | undefined> = {}

    if (!templateGroups) {
      return active
    }

    Object.entries(templateGroups).forEach(([name, versions]) => {
      if (Array.isArray(versions)) {
        active[name] = versions.find(t => t.isActive)
      }
    })

    return active
  }, [templateGroups])

  return {
    // State
    templates,
    templateGroups,
    activeTemplates,
    selectedTemplate,
    templateMetadata,
    preview,
    loading,
    error,

    // Actions
    fetchAllTemplates: (type: string = 'EMAIL') => dispatch(fetchAllTemplates(type)),
    fetchTemplateVersions: (name: string, type?: string) =>
      dispatch(fetchTemplateVersions({ name, type })),
    fetchTemplateById: (id: string) => dispatch(fetchTemplateById(id)),
    createTemplate: (data: CreateNotificationTemplateRequest) =>
      dispatch(createTemplate(data)),
    activateTemplate: (id: string) => dispatch(activateTemplate(id)),
    previewTemplate: (data: TemplatePreviewRequest) =>
      dispatch(previewTemplate(data)),
    deleteTemplate: (id: string) => dispatch(deleteTemplate(id)),
    setSelectedTemplate: (template: typeof selectedTemplate) =>
      dispatch(setSelectedTemplate(template)),
    clearError: () => dispatch(clearError()),
    clearPreview: () => dispatch(clearPreview())
  }
}
